import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import type { MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { beamSolid, boxSolid, horizontalCap, orientedFace, planPoint } from '../solids';
import { hipRoof } from '../roofSolids';
import { defensiveMasonryFace } from '../fortifications/towerMasonry';
import type { MasonryStyles } from './masonry';
import { dominantAxis, meshFrameOf, planBounds, planFrameOf } from './planFrame';

export const BOOM_BLOCKHOUSES = new Set([
  'landmark-blokhuis-boom-west',
  'landmark-blokhuis-boom-east',
]);

/** The painted office at the boom is a compact masonry watch building on the pile barrier.
 * The 6.8 m reservation includes its platform and supports; the exact submerged pile arrangement,
 * invisible sides and dimensions remain estimates. The dark steep hip follows the painted body
 * (museum scan 4290–4430 × 3690–3890), not a generic town house's tiled roof and large windows.
 */
export function buildBoomBlockhouse(
  builder: MeshBuilder,
  landmark: Landmark,
  styles: { readonly masonry: MasonryStyles; readonly timber: VertexStyle }
): number {
  const before = builder.triangleCount;
  const plan = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  const frame = meshFrameOf(plan);
  const bounds = planBounds(plan);
  const { timber, masonry } = styles;
  const u0 = bounds.u0 + 0.4;
  const u1 = bounds.u1 - 0.4;
  const v0 = bounds.v0 + 0.4;
  const v1 = bounds.v1 - 0.4;
  const us = [u0 + 0.2, (u0 + u1) / 2, u1 - 0.2];
  const vs = [v0 + 0.2, v1 - 0.2];
  for (const u of us) {
    for (const v of vs) beamSolid(builder, timber, frame, [u, -2.5, -v], [u, 0.26, -v], 0.24);
    beamSolid(builder, timber, frame, [u, 0.15, -v0], [u, 0.15, -v1], 0.22);
  }
  for (const v of vs) {
    for (let i = 1; i < us.length; i++)
      beamSolid(builder, timber, frame, [us[i - 1]!, -1.6, -v], [us[i]!, -0.5, -v], 0.15);
    beamSolid(builder, timber, frame, [u0, -0.05, -v], [u1, -0.05, -v], 0.24);
  }
  boxSolid(
    builder,
    timber,
    frame,
    [bounds.u0 + 0.08, 0.21, -bounds.v1 + 0.08],
    [bounds.u1 - 0.08, 0.4, -bounds.v0 - 0.08],
    { top: true, bottom: true }
  );

  const lower: Ring = [
    [u0, v0],
    [u1, v0],
    [u1, v1],
    [u0, v1],
  ];
  const upper: Ring = lower.map((p): Vec2 => [
    p[0] + Math.sign((u0 + u1) / 2 - p[0]) * 0.14,
    p[1] + Math.sign((v0 + v1) / 2 - p[1]) * 0.14,
  ]);
  for (let i = 0; i < lower.length; i++) {
    const next = (i + 1) % lower.length;
    const a = lower[i]!;
    const b = lower[next]!;
    const c = upper[next]!;
    const d = upper[i]!;
    orientedFace(
      builder,
      masonry.wall,
      frame,
      [
        planPoint(a[0], 0.4, a[1]),
        planPoint(b[0], 0.4, b[1]),
        planPoint(c[0], 1.0, c[1]),
        planPoint(d[0], 1.0, d[1]),
      ],
      [b[1] - a[1], 0, b[0] - a[0]]
    );
    const width = Math.hypot(c[0] - d[0], c[1] - d[1]);
    defensiveMasonryFace(
      builder,
      masonry.wall,
      frame,
      d,
      c,
      1.0,
      5.6,
      [
        { x: width / 2 - 0.15, y: 0.8, width: 0.3, height: 0.85, cuts: [] },
        ...[0.34, 0.66].map((share) => ({
          x: width * share - 0.22,
          y: 3.4,
          width: 0.44,
          height: 1.25,
          cuts: [],
        })),
      ],
      masonry.opening
    );
  }
  horizontalCap(builder, masonry.wall, frame, lower, 0.4, false);
  hipRoof(builder, masonry.roof, frame, {
    u0: upper[0]![0],
    u1: upper[1]![0],
    v0: upper[0]![1],
    v1: upper[2]![1],
    eaveY: 6.6,
    pitch: (48 * Math.PI) / 180,
  });
  return builder.triangleCount - before;
}
